import { ExportFormatType, ReportFormatType, NetworkProviderFilterType } from "@app/exports/enumerations";
import { FC, useContext, useMemo } from "react";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { UserContext } from "@app/user/UserContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import SharedUsers from "@app/reports/SharedUsers";
import { Link, useHistory } from "react-router-dom";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { ChangedGapsEnum, TimeFrequency } from "@app/projects/enumerators";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import ReportCustomizationContextProvider, { ReportCustomizationContext } from "@app/report-customization/build-reports/ReportCustomizationContext";
import { ReportTypeNamesMap, getFileFormats, getProvidersReportFormats, TimeFrequencyNamesMap, exportTypeToSharedUsersExportTypeMap, sharedUsersAggregateTypeMap } from "@app/report-customization/build-reports/helpers/helpers";
import { ReportCustomizationRunConfiguration } from "@app/report-customization/build-reports/interfaces/reportCustomizationProjectRunConfiguration";
import { ReportCustomizationReducerActions } from "@app/report-customization/build-reports/interfaces/reportCustomizationReducerActionsEnum";
import { ReportCustomizationRunConfigurationNamesEnum } from "@app/report-customization/build-reports/interfaces/reportCustomizationRunConfigurationNamesEnum";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { formatDateTime } from "@app/shared/formatDateTime";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import Roles from "@app/user/roles";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { reportTypeRouteMap } from "@app/report-customization/build-reports/helpers/reportTypeRouteMap";

export const WrappedReportCustomization: FC = () => {
  const rcContext = useContext(ReportCustomizationContext);
  const user = useContext(UserContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const history = useHistory();
  const showChangedGaps = rcContext.state.reportType === ReportType.SpecialtySummaryByCounty
    || (featureFlags.has(Features.ZIP_ANALYSIS_CHANGED_GAPS) && [
      ReportType.ZipAnalysisResultsReport,
      ReportType.ZipClassAggregateResultsReport,
      // ZipAnalysisAdequacyServicingProvider intentionally excluded — changed gaps not applicable to servicing provider
    ].includes(rcContext.state.reportType));
  const isChangedGapsDisabled = !rcContext.state.isCompareMode || !rcContext.state.comparedTimeslice;
  const changedGapsTooltip = isChangedGapsDisabled ? "Compare time slices with changed gaps" : "Selecting a Changed Gaps Report will disable service area and specialty filtering";

  const selectedTimeslice = useMemo(() => {
    return rcContext.findTimeslice(rcContext.state.timeslice);
  }, [rcContext.state.timeslice, rcContext.state.allTimeslices]);

  // Memoize dropdown options
  const reportTypeOptions = useMemo(() => {
    const availableReportTypes: ReportType[] = [];
    const runFrequencyTimeslices = rcContext.state.allTimeslices[rcContext.state.runFrequency];
    const primaryTimeslice = runFrequencyTimeslices.find((t) => t.projectRunConfigurationId.toString() === rcContext.state.timeslice);
    const comparedTimeslice = runFrequencyTimeslices.find((t) => t.projectRunConfigurationId.toString() === rcContext.state.comparedTimeslice);
    const showZipSummaries = !!primaryTimeslice?.hasZipSummaries && (!rcContext.state.isCompareMode || comparedTimeslice?.hasZipSummaries);

    if (rcContext.routeState.isZipProject) {
      availableReportTypes.push(...[
        ReportType.ZipAnalysisAdequacyServicingProvider,
        ReportType.ZipAnalysisResultsReport,
        ReportType.ZipClassAggregateResultsReport,
      ]);
    } else {
      availableReportTypes.push(...[
        ReportType.AdequacyServicingProvider,
        ReportType.CountySummary,
      ]);

      if (rcContext.routeState.showRegionSummaryReport) {
        availableReportTypes.push(ReportType.RegionSummary);
      }

      availableReportTypes.push(...[
        ReportType.StateSummary,
        ReportType.AdequacySpecialtySummary,
        ReportType.AdequacyAggregate,
        ReportType.SpecialtySummaryByCounty,
      ]);
      if (showZipSummaries) {
        availableReportTypes.push(ReportType.ZipSummary);
      }

      if (rcContext.routeState.showClosestProviderToMemberReport) {
        availableReportTypes.push(ReportType.ClosestProvidersToMembers)
      }
    }

    return availableReportTypes.map((type) => {
      return <option key={type} value={type}>{ReportTypeNamesMap.get(type)}</option>;
    });
  }, [selectedTimeslice?.showNextClosestProviders, rcContext.state.timeslice, rcContext.state.comparedTimeslice, rcContext.state.allTimeslices, rcContext.routeState.isZipProject]);

  const showMemberships = useMemo(() => {
    return !!selectedTimeslice?.secondaryMemberSetId;
  }, [selectedTimeslice, rcContext.state.allTimeslices]);

  const membershipItems = useMemo(() => {
    const items: DropdownItem<number>[] = [];
    items.push({ key: rcContext.state.allMemberships[0].key, name: rcContext.state.allMemberships[0].name });
    if (rcContext.state.allMemberships.length > 1) {
      items.push({ key: rcContext.state.allMemberships[1].key, name: rcContext.state.allMemberships[1].name });
    }
    return items;
  }, [rcContext.state.allMemberships]);

  const getTimesliceName = (timeslice: ReportCustomizationRunConfiguration) => {
    // we can't do "Latest Published (monthYear)" because we label the most recent month as latest publish, regardless of whether there's a run
    if (timeslice.name === ReportCustomizationRunConfigurationNamesEnum.LatestPublished && rcContext.state.runFrequency === TimeFrequency.All) {
      return `Latest Published (${formatDateTime(new Date(timeslice.dateCreated))})`;
    } else if (!!timeslice.monthYear && !timeslice.isProjected && !timeslice.isProjectedTermed && !timeslice.isTermed) {
      return timeslice.monthYear;
    } else if (!!timeslice.name) {
      return timeslice.name;
    } else {
      return formatDateTime(new Date(timeslice.dateCreated));
    }
  }

  // selectedTimeslice is either the primary or secondary
  // otherTimeslice is whichever one primary isn't, used to determine if the first one's dropdown option is disabled
  const getTimesliceOptions = (selectedTimeslice: string, otherTimeslice: string) => {
    const inFrequencyOptions = rcContext.state.allTimeslices[rcContext.state.runFrequency].map((option, index) => {
      // the project run config id isn't unique in the case of monthly refreshes with fill months
      return (
        <option
          key={`${option.projectRunConfigurationId}-${index}`}
          value={option.projectRunConfigurationId}
          disabled={option.projectRunConfigurationId.toString() === otherTimeslice || option.isDisabled}
        >
          {getTimesliceName(option)}
        </option>
      );
    });

    if (!!selectedTimeslice
      && rcContext.state.allTimeslices[rcContext.state.runFrequency].length > 0
      && !rcContext.state.allTimeslices[rcContext.state.runFrequency]
        .find((ts) => ts.projectRunConfigurationId.toString() === selectedTimeslice
          || ts.companionProjectRunConfigurationId.toString() === selectedTimeslice)) {
      const missingTimeslice = rcContext.findTimeslice(selectedTimeslice);
      const missingOption = (
        <option
          key={`${missingTimeslice.projectRunConfigurationId}`}
          value={missingTimeslice.projectRunConfigurationId}
          disabled={true}
        >
          {getTimesliceName(missingTimeslice)}
        </option>
      );
      return [missingOption, ...inFrequencyOptions];
    }

    return inFrequencyOptions;
  }

  const primaryTimesliceOptions = useMemo(() => {
    return getTimesliceOptions(rcContext.state.timeslice, rcContext.state.comparedTimeslice);
  }, [rcContext.state.allTimeslices, rcContext.state.timeslice, rcContext.state.comparedTimeslice, rcContext.state.runFrequency]);

  const secondaryTimesliceOptions = useMemo(() => {
    return getTimesliceOptions(rcContext.state.comparedTimeslice, rcContext.state.timeslice);
  }, [rcContext.state.allTimeslices, rcContext.state.timeslice, rcContext.state.comparedTimeslice, rcContext.state.runFrequency]);

  const fileFormatOptions = useMemo(() => {
    const formats = getFileFormats(rcContext.routeState.exportNetworkProviderCount > rcContext.excelRecordsLimit, rcContext.state.reportType);
    return formats.map((f) => <option key={f.key} value={f.key}>{f.value}</option>);
  }, [rcContext.routeState.exportNetworkProviderCount, rcContext.state.reportType]);

  const reportFormatOptions = useMemo(() => {
    const formats = getProvidersReportFormats(user.roles, featureFlags, rcContext.state.providerSource);
    return formats.map((f) => <option key={f.key} value={f.key}>{f.value}</option>);
  }, [user.roles, featureFlags, rcContext.state.providerSource]);

  const runFrequencyOptions = useMemo(() => {
    const availableFrequencies: TimeFrequency[] = [
      TimeFrequency.All,
      TimeFrequency.Monthly,
    ];

    return availableFrequencies.map((frequency) => {
      return <option key={frequency} value={frequency}>{TimeFrequencyNamesMap.get(frequency)}</option>;
    });
  }, []);

  const reportView = useMemo(() => {
    if (reportTypeRouteMap.has(rcContext.state.reportType)) {
      return reportTypeRouteMap.get(rcContext.state.reportType);
    }
  }, [rcContext.state.reportType]);

  const sharedUsersRequiredRoles = useMemo(() => {
    const newRequiredRoles: Roles[] = [];
    if (rcContext.state.reportFormat === ReportFormatType.DISTINCT_SERVICING_PROVIDER_REPORT)
      newRequiredRoles.push(Roles.DISTINCT_SERVICING_USER);

    if (rcContext.state.providerSource === ProviderNetworkTypeEnum.NetNew)
      newRequiredRoles.push(Roles.NET_NEW_PROVIDER_USER);

    if (rcContext.state.appendPatternOfCareProviders)
      newRequiredRoles.push(Roles.PATTERN_OF_CARE_USER);

    return newRequiredRoles;
  }, [rcContext.state.reportFormat, rcContext.state.providerSource, rcContext.state.appendPatternOfCareProviders]);

  return (
    <Page documentTitle="Create Downloadable Report">
      <Breadcrumbs
        template={BreadcrumbTemplateEnum.NetworkAssessment}
        items={[
          { link: `/projects/${rcContext.routeParams.projectId}`, text: rcContext.routeState.projectName },
          rcContext.routeParams.module.charAt(0).toUpperCase() + rcContext.routeParams.module.slice(1),
          ...(rcContext.routeState.filters?.map(m => m.displayText) || [])
          , "Create Report"]}
      />
      <PageInner>
        <PageHeader title="Report Details" disableHR={true}>
          <PageHeaderRight>
            {!!rcContext.routeState.projectName && <QESButton qesType="secondary" onClick={history.goBack} qesTestId="report-customization__back">Return to Project</QESButton>}
            <Link to="/reports" className="qes-button qes-button-primary">Reports</Link>
          </PageHeaderRight>
        </PageHeader>
        <div className="container ms-0">
          <div className="row mb-2">
            <QESSelect
              containerClassName="col-4"
              label="Report Type"
              value={rcContext.state.reportType}
              onChange={(e) => rcContext.changeReportType(e.target.value as ReportType)}
              testId="report-customization__report-type"
              required={true}
              isValid={!!rcContext.state.reportType}
            >
              {reportTypeOptions}
            </QESSelect>

            {!rcContext.routeState.isZipProject && (
              <QESSelect
                containerClassName="col-2"
                label="Run Frequency"
                value={rcContext.state.runFrequency}
                onChange={(e) => rcContext.changeRunFrequency(e.target.value as TimeFrequency)}
                testId="report-customization__run-frequency"
                required={true}
                isValid={!!rcContext.state.runFrequency}
              >
                {runFrequencyOptions}
              </QESSelect>
            )}

            {showMemberships && (
              <MultiselectDropdown
                items={membershipItems}
                label={
                  <>
                    {rcContext.state.allTimeslices.all.length > 1 && (
                      <TooltipWrapper text="Membership comparison is not available while comparing multiple time slices" containerClassName="me-1">
                        <QesIcon iconName={FontAwesome.InfoCircle} description="Hover or focus to show tooltip" width="12" style={{ marginBottom: "2px" }} />
                      </TooltipWrapper>
                    )}
                    Membership
                  </>
                }
                selectedItems={rcContext.state.selectedMemberships}
                testId="report-customization__membership"
                containerClassName="col-4"
                hideSearch={true}
                hideSelectAll={true}
                disabled={rcContext.state.isCompareMode || !!rcContext.state.comparedTimeslice}
                isValid={rcContext.state.reportType === ReportType.ClosestProvidersToMembers ? rcContext.state.selectedMemberships.length === 1 : rcContext.state.selectedMemberships.length > 0}
                required={true}
                customSelectInputText={rcContext.state.selectedMemberships.length === 2 ? "Both" : rcContext.state.selectedMemberships.length === 0 ? "None" : rcContext.state.selectedMemberships[0].name}
                onSelectionChanged={(newMemberships) => rcContext.changeMemberships(newMemberships)}
              />
            )}
          </div>

          {/* Stuff that's in every report */}
          <>
            <div className="row mb-2">
              <QESSelect
                containerClassName="col-4"
                label="Timeslice"
                value={rcContext.state.timeslice}
                onChange={(e) => rcContext.changeTimeslice(e.target.value, false)}
                testId="report-customization__timeslice"
                disabled={primaryTimesliceOptions.length === 0}
                required={true}
                isValid={!!rcContext.state.timeslice}
              >
                {primaryTimesliceOptions}
              </QESSelect>

              {rcContext.state.allTimeslices.all.length > 1 && (rcContext.routeState.showAdvancedComparison || rcContext.routeState.isZipProject) && rcContext.state.reportType !== ReportType.ClosestProvidersToMembers && (
                <>
                  <QESCheckbox
                    containerClassName="col-auto align-self-end"
                    label="Compare with"
                    checked={rcContext.state.isCompareMode}
                    onChange={(e) => rcContext.changeIsCompareMode(e.target.checked)}
                    testId="report-customization__compare-to"
                    disabled={rcContext.state.selectedMemberships.length > 1}
                  />

                  <QESSelect
                    containerClassName="col-4"
                    label="Timeslice"
                    value={rcContext.state.comparedTimeslice}
                    onChange={(e) => rcContext.changeTimeslice(e.target.value, true)}
                    testId="report-customization__compared-timeslice"
                    disabled={!rcContext.state.isCompareMode || secondaryTimesliceOptions.length === 0}
                  >
                    {secondaryTimesliceOptions}
                  </QESSelect>

                  {showChangedGaps && (
                    <QESSelect
                      containerClassName="col-2"
                      label={
                        <>
                          <TooltipWrapper text={changedGapsTooltip} containerClassName="me-1">
                            <QesIcon iconName={FontAwesome.InfoCircle} description="Hover or focus to show tooltip" width="12" style={{ marginBottom: "2px" }} />
                          </TooltipWrapper>
                          Changed Gaps
                        </>
                      }
                      value={rcContext.state.changedGapsType}
                      onChange={(e) => rcContext.changeChangedGapsType(e.target.value as ChangedGapsEnum || "")}
                      testId="report-customization__changed-gaps"
                      disabled={isChangedGapsDisabled}
                    >
                      <option value=""></option>
                      <option value={ChangedGapsEnum.All}>All Changed Gaps</option>
                      <option value={ChangedGapsEnum.Opened}>Opened Gaps</option>
                      <option value={ChangedGapsEnum.Closed}>Closed Gaps</option>
                    </QESSelect>
                  )}
                </>
              )}
            </div>

            <div className="row mb-2">
              <QESTextInput
                containerClassName="col-5"
                label="File Name"
                testId="report-customization__file-name"
                value={rcContext.state.filename}
                onChange={(e) => rcContext.dispatch({ payload: { filename: e.target.value }, type: ReportCustomizationReducerActions.ChangeFilename })}
                required={true}
                isValid={!!rcContext.state.filename}
              />

              <QESSelect
                containerClassName="col-4"
                label="File Format"
                value={rcContext.state.fileFormat}
                onChange={(e) => rcContext.changeFileFormat(e.target.value as ExportFormatType)}
                testId="report-customization__file-format"
                required={true}
                isValid={!!rcContext.state.fileFormat}
              >
                {fileFormatOptions}
              </QESSelect>

              {rcContext.state.reportType === ReportType.AdequacyServicingProvider
                && (
                  <QESSelect
                    containerClassName="col-3"
                    label="Report Format"
                    value={rcContext.state.reportFormat}
                    onChange={(e) => rcContext.changeReportFormat(e.target.value as ReportFormatType)}
                    testId="report-customization__report-format"
                    disabled={rcContext.state.fileFormat === ExportFormatType.Excel || rcContext.state.fileFormat === ExportFormatType.Pipe}
                    required={true}
                    isValid={!!rcContext.state.reportType}
                  >
                    {reportFormatOptions}
                  </QESSelect>
                )}
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <SharedUsers
                  exportType={exportTypeToSharedUsersExportTypeMap.get(rcContext.state.reportType)}
                  customProjectIds={[parseInt(rcContext.routeParams.projectId)]}
                  tooltipTitle="Users in this list have been filtered to only show users with access to this project and information in this report"
                  networkProviderFilterType={rcContext.state.providerFilter as NetworkProviderFilterType}
                  aggregateType={sharedUsersAggregateTypeMap.get(rcContext.state.reportType)}
                  updateSelectedSharedUsers={(newSharedUsers) => rcContext.dispatch({ payload: { sharedUsers: newSharedUsers }, type: ReportCustomizationReducerActions.ChangeSharedUsers })}
                  sharedUsersFilter={rcContext.state.sharedUsers}
                  containerClassnames="d-block"
                  requiredRoles={sharedUsersRequiredRoles}
                />
              </div>

              <QESCheckbox
                containerClassName="col-auto align-self-end"
                label="Email me a link to the report once it's ready."
                checked={rcContext.state.shouldEmailLink}
                onChange={(e) => rcContext.dispatch({ payload: { shouldEmailLink: e.target.checked }, type: ReportCustomizationReducerActions.ChangeShouldEmailLink })}
                testId="report-customization__should-email-link"
              />
            </div>
          </>
        </div>

        {reportView}
      </PageInner>
    </Page>
  )
}

export const ReportCustomization: FC = () => {
  return (
    <ReportCustomizationContextProvider>
      <WrappedReportCustomization />
    </ReportCustomizationContextProvider>
  );
};
